import React, { useState } from 'react';
import {
    Card,
    CardMedia,
    CardContent,
    Typography,
    Chip,
    Box,
    Button,
} from '@mui/material';

const getInitialsImage = (name) => {
    if (!name) return '';
    const initials = name
        .split(' ')
        .map((word) => word[0]?.toUpperCase())
        .join('')
        .slice(0, 2);
    return `https://ui-avatars.com/api/?name=${initials}&background=random&color=fff&bold=true`;
};

const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return isNaN(date)
        ? 'Invalid Date'
        : date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
};

const ProjectCard = ({ project }) => {
    const {
        clientName,
        address,
        startDate,
        completionDate,
        services = [],
        photo,
        video,
    } = project;

    const [showVideo, setShowVideo] = useState(false);

    return (
        <Card
            sx={{
                width: 300, // ✅ Fixed width
                height: 450, // ✅ Fixed height
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                borderRadius: 2,
                boxShadow: 4,
                overflow: 'hidden',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 8,
                },
            }}
        >
            {!showVideo && (
                <CardMedia
                    component="img"
                    height="150"
                    image={photo || getInitialsImage(clientName)}
                    alt={`${clientName} Project`}
                    sx={{
                        objectFit: 'cover',
                        width: '100%',
                    }}
                />
            )}

            <CardContent
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1,
                }}
            >
                <Typography variant="h6" fontWeight={600} noWrap>
                    {clientName}
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                    {address}
                </Typography>

                <Box mt={1}>
                    <Typography variant="caption" color="text.secondary">
                        <strong>Start:</strong> {formatDate(startDate)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        <strong>End:</strong> {formatDate(completionDate)}
                    </Typography>
                </Box>

                {services.length > 0 && (
                    <Box mt={1} display="flex" flexWrap="wrap" gap={0.5} maxHeight={60} overflow="auto">
                        {services.map((service, index) => (
                            <Chip
                                key={index}
                                label={service}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        ))}
                    </Box>
                )}

                {video && !showVideo && (
                    <Box mt="auto">
                        <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            onClick={() => setShowVideo(true)}
                        >
                            Watch Video
                        </Button>
                    </Box>
                )}

                {video && showVideo && (
                    <Box mt={1}>
                        <Box
                            component="video"
                            width="100%"
                            height="150"
                            controls
                            muted
                            loop
                            sx={{ borderRadius: 1.5, backgroundColor: '#000' }}
                        >
                            <source src={video} type="video/mp4" />
                            Your browser does not support the video tag.
                        </Box>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default ProjectCard;
